import React, { useState } from 'react';
import { Button, Form, Message } from 'semantic-ui-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API, copy, showError } from '../helpers';
import AuthLayout from '../layout/AuthLayout';

const PasswordResetConfirm = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [copyState, setCopyState] = useState('idle');
  const validLink = Boolean(email && token);

  const handleSubmit = async () => {
    if (!validLink || loading || newPassword) return;
    setLoading(true);
    try {
      const res = await API.post('/api/user/reset', { email, token });
      if (!res?.data) return;
      const { success, message, data } = res.data;
      if (success) {
        setNewPassword(data);
      } else {
        showError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!newPassword) return;
    const copied = await copy(newPassword);
    setCopyState(copied ? 'success' : 'error');
  };

  return (
    <AuthLayout
      title={t('auth.reset.confirm.title')}
      description={t('auth.reset.confirm.subtitle')}
    >
      {!validLink ? (
        <Message negative role='alert'>
          <Message.Header>{t('auth.reset.confirm.invalid_title')}</Message.Header>
          <p>{t('auth.reset.confirm.invalid_description')}</p>
          <div className='fvv-auth-footer'>
            <Link to='/reset'>{t('auth.reset.confirm.request_again')}</Link>
          </div>
        </Message>
      ) : newPassword ? (
        <Message success role='status'>
          <Message.Header>{t('auth.reset.confirm.result_title')}</Message.Header>
          <p>{t('auth.reset.confirm.result_description')}</p>
          <p>
            <strong>{t('auth.reset.email')}:</strong> {email}
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              margin: '16px 0',
              flexWrap: 'wrap',
            }}
          >
            <code
              style={{
                flex: 1,
                minWidth: 0,
                padding: '10px 12px',
                borderRadius: 8,
                background: 'var(--fvv-bg)',
                border: '1px solid var(--fvv-line)',
                wordBreak: 'break-all',
              }}
            >
              {newPassword}
            </code>
            <Button type='button' onClick={handleCopy}>
              <i
                className={`${copyState === 'success' ? 'check' : 'copy outline'} icon`}
                aria-hidden='true'
              />
              {copyState === 'success'
                ? t('auth.reset.confirm.copied')
                : t('auth.reset.confirm.copy')}
            </Button>
          </div>
          {copyState === 'error' && (
            <p style={{ color: 'var(--fvv-danger)', fontSize: 13 }}>
              {t('auth.reset.confirm.copy_failed')}
            </p>
          )}
          <Button fluid primary type='button' disabled>
            {t('auth.reset.confirm.button_disabled')}
          </Button>
          <div className='fvv-auth-footer'>
            <Link to='/login'>{t('auth.reset.back_login')}</Link>
          </div>
        </Message>
      ) : (
        <Form size='large' className='fvv-form' onSubmit={handleSubmit}>
          <Form.Input
            fluid
            icon='mail outline'
            iconPosition='left'
            value={email}
            readOnly
            aria-label={t('auth.reset.email')}
          />
          <Button
            fluid
            primary
            type='submit'
            loading={loading}
            disabled={loading}
          >
            {loading
              ? t('auth.reset.confirm.processing')
              : t('auth.reset.confirm.button')}
          </Button>
          <p className='fvv-auth-footer'>{t('auth.reset.confirm.notice')}</p>
        </Form>
      )}
    </AuthLayout>
  );
};

export default PasswordResetConfirm;
